import React, { useEffect, useRef, useState, useMemo } from "react";
import { Helmet } from "react-helmet";
import "material-icons/iconfont/material-icons.css";
import WordCard from "../../components/WordCard";
import { useStyles } from "./WordBrowser.styles";

const DEFAULT_LIMIT = 30;

const WordBrowser = () => {
  const classes = useStyles();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");

  const sentinelRef = useRef(null);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Split items into word of the day and rest
  const wordOfTheDay = items[0] || null;
  const restOfWords = items.slice(1);

  // Filter rest of words based on debounced search query
  const filteredWords = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return restOfWords;

    const query = debouncedSearchQuery.toLowerCase();
    return restOfWords.filter((word) => {
      const title = (word.title || "").toLowerCase();
      const definition = (word.definition || "").toLowerCase();
      return title.includes(query) || definition.includes(query);
    });
  }, [restOfWords, debouncedSearchQuery]);

  const loadNext = () => {
    if (loading || !hasMore) return;

    setLoading(true);
    setError("");

    fetch(`/api/words?page=${page}&limit=${DEFAULT_LIMIT}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        const nextItems = Array.isArray(data.items) ? data.items : [];
        setItems((prev) => prev.concat(nextItems));
        setHasMore(Boolean(data.hasMore));
        setPage((p) => p + 1);
      })
      .catch(() => setError("Failed to load words."))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNext();
  }, []);

  useEffect(() => {
    const el = sentinelRef.current;
    let observer = null;

    if (el) {
      observer = new IntersectionObserver(
        (entries) => {
          const first = entries && entries[0];
          if (first && first.isIntersecting) loadNext();
        },
        { root: null, rootMargin: "600px 0px", threshold: 0 }
      );

      observer.observe(el);
    }

    return () => {
      if (observer) observer.disconnect();
    };
  }, [hasMore, loading]);

  return (
    <>
      <div className={classes.container}>
        <Helmet>
          <title>Browse Words | Word Hopper</title>
          <link rel="canonical" href="https://www.wordhopnow.com/words" />
          <meta
            name="description"
            content="Browse Word Hopper dictionary entries."
          />
        </Helmet>

        {error ? <p className={classes.error}>{error}</p> : null}

        {items.length > 0 ? (
          <h1 className={classes.title}>Browse Words</h1>
        ) : null}

        {/* Word of the Day */}
        {wordOfTheDay ? (
          <div className={classes.featuredWordContainer}>
            <WordCard word={wordOfTheDay} featured />
          </div>
        ) : null}

        {/* Initial loading state */}
        {items.length === 0 && loading ? (
          <div className={classes.loadingContainer}>
            <p>Loading…</p>
          </div>
        ) : null}
      </div>

      {items.length > 0 ? (
        <div className={classes.searchAndResultsWrapper}>
          <div className={classes.innerWrapper}>
            {/* Search Bar */}
            <div className={classes.searchContainer}>
              <span className={`material-icons-outlined ${classes.searchIcon}`}>
                search
              </span>
              <input
                name="search"
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={classes.searchInput}
              />
            </div>

            {/* Results Grid */}
            <div className={classes.grid}>
              {filteredWords.map((w) => (
                <WordCard key={w.slug || w.id} word={w} />
              ))}

              <div ref={sentinelRef} className={classes.sentinel} />
            </div>

            {debouncedSearchQuery && filteredWords.length === 0 ? (
              <p className={classes.noResults}>No words match your search.</p>
            ) : null}

            <div className={classes.loadingContainer}>
              {loading ? <p>Loading…</p> : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default WordBrowser;
